// Admin AI assistant sidebar — a general-purpose chat window opened from the
// logomark next to the account name in the AppBar. Admin-only (both here and at
// /api/admin/assistant/chat, which is the gate that actually matters).
//
// Nothing is persisted: the transcript lives in component state and is posted
// back in full on every turn, because the route is stateless. "Closing it
// forgets" is the clearest possible answer to "where did my question go?", and
// it matters more now than it did when this was a plain scratchpad: the
// assistant can reach conversation data and platform-wide analytics, so a
// transcript is a record of what someone asked about the business. Not storing
// it is the simplest way not to have to govern it.
//
// Three independent access switches, deliberately not collapsed into one
// "smart" toggle — each is a different question with a different cost:
//   Use the open conversation   — one contact's operational record.
//   Include content             — that contact's PII-redacted text.
//   Query platform data         — read-only aggregates across every contact.
//
// Like ManualActionsSidebar this is a plain fixed-position Box rather than an
// MUI Drawer, so it never traps focus or blocks clicks — an admin needs to keep
// reading the page behind it while typing.

import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box, Stack, Typography, TextField, IconButton, CircularProgress, Alert, Tooltip, Button,
  FormControlLabel, Checkbox, Divider, Select, MenuItem,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { askAssistant, exportAssistantReport, fetchAssistantModels } from './api.js'
import { ResizeHandle } from './ResizablePane.jsx'

// Running cost is shown to four decimal places, not two: a single turn is
// routinely worth a fraction of a cent, and rounding that to $0.00 would make
// the readout look broken exactly when it is working.
function formatUsd(usd) {
  if (usd >= 1) return `$${usd.toFixed(2)}`
  return `$${usd.toFixed(4)}`
}

// OpenAI's logomark. The assistant runs on Azure OpenAI (gpt-5.6-terra via
// Foundry), so the mark names what's actually behind the button.
export function OpenAiIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.3639 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </svg>
  )
}

// The AppBar button that opens/closes the sidebar.
export function AssistantChatButton({ open, onToggle }) {
  return (
    <Tooltip title={open ? 'Close AI assistant' : 'Ask AI'}>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-label={open ? 'Close AI assistant' : 'Open AI assistant'}
        aria-expanded={open}
        data-testid="assistant-chat-button"
        sx={{ color: open ? 'primary.main' : 'text.secondary' }}
      >
        <OpenAiIcon size={18} />
      </IconButton>
    </Tooltip>
  )
}

function Bubble({ role, content }) {
  const isUser = role === 'user'
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <Box
        sx={(theme) => ({
          maxWidth: '88%',
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          bgcolor: isUser ? 'primary.main' : alpha(theme.palette.text.primary, 0.06),
          color: isUser ? theme.palette.primary.contrastText : 'text.primary',
          // Model output is plain text with meaningful line breaks; render them
          // as-is rather than pulling in a markdown renderer.
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '0.85rem',
          lineHeight: 1.5,
        })}
      >
        {content}
      </Box>
    </Box>
  )
}

// width/onResizeStart come from Shell: the panel is docked, so the layout needs
// the width to shrink by exactly as much as this takes up.
export default function AssistantChat({ onClose, conversationId, width, onResizeStart }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Ground answers in the open conversation. On by default when there is one —
  // it's the reason to open the sidebar from a thread — but message TEXT is
  // opt-in on top of that, and stays off until asked for every session.
  const [useConversation, setUseConversation] = useState(true)
  const [includeMessageText, setIncludeMessageText] = useState(false)
  // Platform-wide analytics. Off by default and per-session like the text tier:
  // a system query costs real model rounds and real database work, so it is a
  // thing you turn on to ask a trending question, not a thing that runs behind
  // every "what does this field mean".
  const [systemAnalytics, setSystemAnalytics] = useState(false)
  const [notices, setNotices] = useState([])
  const [queriesRun, setQueriesRun] = useState([])
  const [exporting, setExporting] = useState(false)
  const [copied, setCopied] = useState(false)
  // Which deployment to run on. Null until /models answers, which is also how
  // the picker stays hidden when only one deployment is selectable.
  const [models, setModels] = useState([])
  const [model, setModel] = useState('')
  // Running spend for THIS conversation. `unpriced` counts turns we could not
  // price, so a partial total is never presented as a complete one.
  const [spend, setSpend] = useState({ usd: 0, tokens: 0, cached: 0, unpriced: 0 })
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  // Lets Stop cancel the in-flight turn. A ref, not state: aborting must not
  // wait for a re-render, and the controller is not rendered.
  const abortRef = useRef(null)

  // Keep the newest turn in view as the transcript grows.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, busy])

  useEffect(() => { inputRef.current?.focus() }, [])

  // Which deployments this admin may pick. A failure here is not worth an error
  // banner — the assistant works fine on the server's default, you just don't
  // get to change it — so it degrades to no picker.
  useEffect(() => {
    let cancelled = false
    fetchAssistantModels()
      .then(({ models: list, defaultModel }) => {
        if (cancelled) return
        setModels(list ?? [])
        setModel(defaultModel ?? '')
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Abort any in-flight turn if the panel unmounts (admin closed the sidebar or
  // lost the admin role), so the server stops working on an answer that now has
  // nowhere to go.
  useEffect(() => () => abortRef.current?.abort(), [])

  const send = useCallback(async () => {
    const text = input.trim()
    if (!text || busy) return
    const next = [...messages, { role: 'user', content: text }]
    setMessages(next)
    setInput('')
    setError(null)
    setBusy(true)
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const grounded = useConversation && conversationId
      const { reply, notices: n, queriesRun: q, usage, cost } = await askAssistant(next, {
        context: grounded ? { conversationId, includeMessageText } : undefined,
        systemAnalytics,
        model: model || undefined,
        signal: controller.signal,
      })
      setNotices(n ?? [])
      setQueriesRun(q ?? [])
      setMessages([...next, { role: 'assistant', content: reply }])
      // Accumulate spend. An unpriced turn increments a counter rather than
      // adding 0 — otherwise a conversation with no price table would read as
      // free rather than as unknown.
      setSpend((s) => ({
        usd: s.usd + (cost?.priced ? cost.usd : 0),
        tokens: s.tokens + (usage?.total_tokens ?? 0),
        // Cached input is billed far cheaper, and an analytics question re-sends
        // the same tool schema every round — so this is usually the reason a
        // multi-round answer costs less than it looks like it should.
        cached: s.cached + (cost?.cachedTokens ?? 0),
        unpriced: s.unpriced + (cost?.priced ? 0 : 1),
      }))
    } catch (err) {
      // Leave the user's turn in the transcript and put their text back in the
      // box, so a failed send is one keypress from a retry rather than a
      // retype. The failed turn is dropped — resending it is what Retry does.
      setMessages(messages)
      setInput(text)

      // A deliberate Stop is not an error. Restoring the question to the box
      // (above) is the whole recovery: press Enter to ask it again.
      if (err.name === 'AbortError') return
      // Name the failing stage. "Unavailable" alone sent someone hunting through
      // container logs to find out the model call was refused — the code costs
      // nothing to show and is the difference between guessing and knowing.
      // It's our own error string, never the provider's (that can carry the
      // endpoint host and deployment name).
      const code = err.body?.error
      setError(
        err.status === 429 ? 'Too many requests — wait a moment and try again.'
          : err.status === 403 ? 'The AI assistant is admin-only.'
            : code === 'conversation_too_long' ? 'This chat has grown too long. Start a new one.'
              : code === 'conversation_not_found' ? 'That conversation no longer exists.'
                : code === 'context_unavailable' ? "Couldn't load the conversation data (context_unavailable)."
                  : code === 'assistant_unavailable' ? 'The model call was refused or timed out (assistant_unavailable). Check the server logs.'
                    : code === 'unknown_model' ? 'That model is no longer available — pick another.'
                      : `The assistant is unavailable right now${code ? ` (${code})` : ''}.`,
      )
    } finally {
      abortRef.current = null
      setBusy(false)
    }
  }, [input, busy, messages, useConversation, includeMessageText, systemAnalytics, model, conversationId])

  // Stop the in-flight turn. Aborting the fetch closes the socket, which the
  // server watches — so this stops the work, not just the waiting.
  const stop = useCallback(() => abortRef.current?.abort(), [])

  // Clear the conversation. Also cancels anything in flight: leaving a turn
  // running whose transcript has been thrown away would bill for an answer that
  // has nowhere to land.
  const clearConversation = useCallback(() => {
    abortRef.current?.abort()
    setMessages([])
    setError(null)
    setNotices([])
    setQueriesRun([])
    setSpend({ usd: 0, tokens: 0, cached: 0, unpriced: 0 })
    inputRef.current?.focus()
  }, [])

  // Copy the contact record as a pasteable report. Built server-side from the
  // same context the assistant sees, so the export can never show more than the
  // assistant was allowed to — and the session goes with it, so whoever reads it
  // gets the diagnosis next to the evidence.
  const copyReport = useCallback(async () => {
    if (!conversationId || exporting) return
    setExporting(true)
    setError(null)
    try {
      const report = await exportAssistantReport({ conversationId, includeMessageText }, messages)
      await navigator.clipboard.writeText(report)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      // A clipboard failure is not a data failure — say which happened, or the
      // reader assumes the export itself broke.
      setError(err.name === 'NotAllowedError'
        ? 'The report was built but the browser blocked the clipboard write.'
        : `Could not build the report${err.body?.error ? ` (${err.body.error})` : ''}.`)
    } finally {
      setExporting(false)
    }
  }, [conversationId, includeMessageText, messages, exporting])

  return (
    <Box
      data-testid="assistant-chat-panel"
      sx={{
        position: 'fixed', top: 0, bottom: 0, right: 0, width,
        zIndex: 1200, display: 'flex', bgcolor: 'background.paper', boxShadow: 4,
      }}
    >
      <ResizeHandle onMouseDown={onResizeStart} label="Resize AI assistant" />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <OpenAiIcon size={16} />
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>AI Assistant</Typography>
          {conversationId && (
            <Tooltip title="Copy this contact's record — plus this session — as a report you can paste into an issue or hand to a coding agent">
              <span>
                <Button
                  size="small" sx={{ fontSize: '0.7rem' }}
                  onClick={copyReport}
                  disabled={exporting}
                  data-testid="assistant-copy-report"
                >
                  {copied ? 'Copied' : exporting ? 'Building…' : 'Copy report'}
                </Button>
              </span>
            </Tooltip>
          )}
          {/* Always present, not only once a transcript exists: "how do I start
              over" should never depend on already having started. */}
          <Tooltip title="Clear this conversation — the transcript, its notices, and the running cost">
            <span>
              <Button
                size="small" sx={{ fontSize: '0.7rem' }}
                onClick={clearConversation}
                disabled={messages.length === 0 && !busy}
                data-testid="assistant-clear"
              >
                Clear
              </Button>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose} aria-label="Close AI assistant">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </IconButton>
        </Stack>

        {/* Data-access controls. Two independent switches, because they are two
            different decisions: whether the assistant sees this conversation at
            all, and whether it sees what was actually said in it. */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          {conversationId ? (
            <>
              <FormControlLabel
                control={<Checkbox size="small" checked={useConversation} onChange={(e) => setUseConversation(e.target.checked)} />}
                label={<Typography variant="caption">Use the open conversation</Typography>}
              />
              <Box sx={{ pl: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={includeMessageText}
                      disabled={!useConversation}
                      onChange={(e) => setIncludeMessageText(e.target.checked)}
                      inputProps={{ 'data-testid': 'assistant-include-text' }}
                    />
                  }
                  label={<Typography variant="caption">Include content: messages, AI drafts, QA notes, API payloads (PII-redacted)</Typography>}
                />
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: -0.5 }}>
                  {includeMessageText
                    ? 'Names, addresses, emails, phones and card numbers are masked before the model sees anything. If masking is unavailable, content is withheld rather than sent. Request headers are never included.'
                    : 'Off: status, intent, order, QA scores and the activity log (what ran, how long, what failed) — but no message text, drafts, QA notes or API bodies.'}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No conversation open — tick “Query platform data” below to ask about the system as a whole.
            </Typography>
          )}

          {/* The third switch, and a different kind of access from the two
              above: those widen what the assistant sees about ONE contact,
              this lets it query every contact. Kept visually separate for
              that reason rather than nested under the conversation controls. */}
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={systemAnalytics}
                onChange={(e) => setSystemAnalytics(e.target.checked)}
                inputProps={{ 'data-testid': 'assistant-system-analytics' }}
              />
            }
            label={<Typography variant="caption">Query platform data (volumes, intents, agents, QA, automation)</Typography>}
          />
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', pl: 3, mt: -0.5 }}>
            {systemAnalytics
              ? 'Runs read-only queries across every account to answer trending questions. Message text stays PII-redacted and is only read when the assistant samples contacts. Answers list the queries they were built from.'
              : 'Off: the assistant cannot see anything beyond the open conversation.'}
          </Typography>
        </Box>

        <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, py: 1.5 }}>
          {messages.length === 0 && !busy ? (
            <Stack spacing={1} sx={{ color: 'text.secondary', mt: 2 }}>
              <Typography variant="body2">Ask anything.</Typography>
              <Typography variant="caption">
                The assistant can read the open conversation's status, intent, order and
                timings — and its message text only if you tick the box above. It can't take
                any action, and nothing here is saved.
              </Typography>
              {systemAnalytics && (
                <Typography variant="caption">
                  Platform queries are on. Try: “what intents grew most vs last month?”,
                  “sample 50 random contacts from last week — are buyers happy or frustrated?”,
                  “which CSRs edit the AI draft most, and on which intents?”
                </Typography>
              )}
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              {messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} />)}
              {busy && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption">Thinking…</Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Box>

        {/* Anything the server withheld is surfaced verbatim — an omission the
            admin can't see reads as an absence, which is the failure mode that
            matters most when the answer is "there's nothing there." */}
        {/* What the answer was actually built from. An analytical claim with no
            visible provenance is indistinguishable from a confident guess, and
            the whole point of the tool layer is that it isn't one. */}
        {queriesRun.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 1.5, py: 0.75 }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                Built from {queriesRun.length} quer{queriesRun.length === 1 ? 'y' : 'ies'}
              </Typography>
              {queriesRun.map((q, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  • {q.tool.replace(/_/g, ' ')}{q.entity ? ` — ${q.entity}` : ''}{q.window ? ` — ${q.window}` : ''}{q.rows != null ? ` (${q.rows} rows)` : ''}
                </Typography>
              ))}
            </Box>
          </>
        )}

        {notices.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 1.5, py: 0.75, maxHeight: 200, overflowY: 'auto' }}>
              {notices.map((n, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>• {n}</Typography>
              ))}
            </Box>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mx: 1.5, mb: 1, py: 0 }} onClose={() => setError(null)}>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        )}

        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <TextField
            inputRef={inputRef}
            fullWidth multiline maxRows={8} size="small"
            placeholder="Ask a question…  (Enter to send, Shift+Enter for a new line)"
            value={input}
            // Deliberately NOT disabled while busy: you can type your next
            // question while one is running, and Stop is one click away.
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); busy ? stop() : send() }
            }}
            inputProps={{ 'data-testid': 'assistant-chat-input' }}
          />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
            {/* Model picker, only when there is genuinely a choice to make. */}
            {models.length > 1 && (
              <Select
                size="small"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={busy}
                variant="standard"
                disableUnderline
                sx={{ fontSize: '0.7rem', color: 'text.secondary', '& .MuiSelect-select': { py: 0.25 } }}
                inputProps={{ 'data-testid': 'assistant-model-select' }}
              >
                {models.map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.75rem' }}>
                    {m.id}{m.isDefault ? ' (default)' : ''}
                    {m.pricePerMillion ? '' : ' · unpriced'}
                  </MenuItem>
                ))}
              </Select>
            )}

            {/* Running cost for this conversation. Hidden until something has
                actually been spent, so an untouched sidebar isn't cluttered
                with a zero. */}
            {(spend.tokens > 0) && (
              <Tooltip title={
                spend.unpriced > 0
                  ? `${spend.unpriced} turn${spend.unpriced === 1 ? '' : 's'} ran on a deployment with no configured price, so they are NOT included in this total. Estimated from reported token usage; the Costing view is the authority on real spend.`
                  : `Estimated from reported token usage at configured prices${spend.cached > 0 ? `, including ${spend.cached.toLocaleString()} cached input tokens billed at the cheaper cached rate` : ''}. The Costing view is the authority on real spend.`
              }>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="assistant-spend">
                  {spend.unpriced > 0 && spend.usd === 0
                    ? `${spend.tokens.toLocaleString()} tokens · cost unknown`
                    : `≈ ${formatUsd(spend.usd)} · ${spend.tokens.toLocaleString()} tokens${spend.unpriced > 0 ? ' +unpriced' : ''}`}
                </Typography>
              </Tooltip>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {busy ? (
              <Button
                size="small" variant="outlined" color="error"
                onClick={stop}
                data-testid="assistant-stop"
              >
                Stop
              </Button>
            ) : (
              <Button size="small" variant="contained" onClick={send} disabled={!input.trim()}>
                Send
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
